'use client';

/**
 * useReferralCapture Hook
 *
 * Story 6.5: Viral Tracking and Attribution
 *
 * Captures referral attribution from URL parameters on mount.
 * Used in landing pages to track visitors coming from "Made with Boty" links.
 */

import { useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { captureReferral, getReferral } from '../lib/referralTracking';

interface UseReferralCaptureOptions {
  /** Called when a new referral is captured */
  onCapture?: (botId: string) => void;
  /** Whether to capture referrals (defaults to true) */
  enabled?: boolean;
}

interface UseReferralCaptureResult {
  /** The current referral bot ID (if any) */
  referralBotId: string | null;
}

/**
 * Hook to capture referral attribution from URL parameters.
 *
 * Looks for `?ref=<bot_id>` parameter and stores it for attribution.
 * The referral persists for 7 days and is used when creating new bots.
 *
 * @example
 * ```tsx
 * function LandingPage() {
 *   const { referralBotId } = useReferralCapture({
 *     onCapture: (botId) => {
 *       trackLandingPageVisit(botId);
 *     }
 *   });
 *
 *   return <div>...</div>;
 * }
 * ```
 */
export function useReferralCapture(
  options: UseReferralCaptureOptions = {}
): UseReferralCaptureResult {
  const { onCapture, enabled = true } = options;
  const searchParams = useSearchParams();

  // Use state to avoid SSR hydration mismatch (localStorage is client-only)
  const [referralBotId, setReferralBotId] = useState<string | null>(null);

  // Load existing referral on mount (client-side only)
  useEffect(() => {
    setReferralBotId(getReferral());
  }, []);

  // Capture referral from URL parameter
  useEffect(() => {
    if (!enabled) return;

    const refParam = searchParams.get('ref');
    const variantParam = searchParams.get('variant') ?? undefined;

    // Story 6.11: New viral CTA format: ?ref=viral_cta&variant={variant}&bot_id={bot_id}
    if (refParam === 'viral_cta') {
      const botIdParam = searchParams.get('bot_id');
      if (botIdParam) {
        captureReferral(botIdParam, variantParam);
        setReferralBotId(botIdParam);
        if (onCapture) {
          onCapture(botIdParam);
        }
      }
      return;
    }

    // Legacy format: ?ref={botId}
    if (refParam) {
      captureReferral(refParam, variantParam);
      setReferralBotId(refParam);
      if (onCapture) {
        onCapture(refParam);
      }
    }
  }, [searchParams, onCapture, enabled]);

  // Return current referral (may be from URL or previously stored)
  return {
    referralBotId,
  };
}
